import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Container, Row, Col, Card, CardBody, Input, Button, Table,
    Modal, ModalHeader, ModalBody, Form, FormGroup, Label, InputGroup,
    FormFeedback, Spinner, Pagination, PaginationItem, PaginationLink,
    Badge,
    ModalFooter,
    Dropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
    Alert,
    ListGroup,
    ListGroupItem
} from 'reactstrap';
import {
    FaSearch, FaBarcode, FaCube, FaBoxes, FaUser, FaPlus, FaTimes,
    FaAngleDoubleLeft, FaAngleLeft, FaAngleRight, FaAngleDoubleRight,
    FaFileExcel, FaEye, FaEdit, FaTrash, FaSave,
    FaFilter,
    FaList,
    FaLock
} from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import moment from 'moment';
import { productActions } from '../../sagas/productSlice';
import { debounce, set } from 'lodash';
import { inventoryActions } from '../../sagas/inventorySlice';
import * as XLSX from 'xlsx';

const InventoryPage = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [currentPage, setCurrentPage] = useState(1);

    // Product Search and Selection
    const [searchTerm, setSearchTerm] = useState('');
    const {
        products,
        loading: productLoading,
        totalPages,
        totalElements,
        pageNumber,
        pageSize,
        error: productError,
    } = useSelector((state) => state.products);

    const searchInputRef = useRef(null);
    const [isSearching, setIsSearching] = useState(false);
    const { fullName } = useSelector(state => state.auth);

    const [selectedProductDetails, setSelectedProductDetails] = useState({});
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedProductIds, setSelectedProductIds] = useState([]);


    const [localStockReel, setLocalStockReel] = useState({});
    // Modal State

    const highlightedItem = useSelector((state) => state.inventory.highlightedItem);
    const loading = useSelector((state) => state.inventory.loading);
    const error = useSelector((state) => state.inventory.error);
    const inventories = useSelector((state) => state.inventory.inventories);
    const inventory = useSelector((state) => state.inventory.inventory);
    const quantityInputRef = useRef(null);
    const [dropdownOpen, setDropdownOpen] = useState(false);

    useEffect(() => {
        dispatch(inventoryActions.findAll());

        // Auto focus on mount
        setTimeout(() => {
            searchInputRef.current?.focus();
        }, 0);
        // Add keyboard shortcut
        const handleKeyDown = (e) => {
            if (e.key === 'F2') {
                setSearchTerm(''); // Clear search input after adding
                e.preventDefault();
                searchInputRef.current?.focus();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    useEffect(() => {
        if (isSearching && searchTerm.length >= 3) {
            const searchLower = searchTerm.toLowerCase();
            setIsSearching(false)
            // Check for exact barcode match

            const matchedItems = products?.filter(p =>
                p.barcode?.toLowerCase().includes(searchLower) ||
                p.code?.toLowerCase().includes(searchLower) ||
                p.description?.toLowerCase().includes(searchLower)
            );


            if (products.length === 1) {
                // Add to cart directly
                handleAddProduct(matchedItems[0]);
                if (searchTerm.length > 8) { // Assuming barcode length is 13
                    setSearchTerm('');
                    //searchInputRef.current?.focus();
                }
            } else if (totalPages === 1 && matchedItems.length > 1 && searchTerm.length >= 3) {
                // Show modal with search results
                //setSearchResults();
                setIsModalOpen(true);
            } else {
                //setSearchResults([]);
            }
        }

    }, [products]);


    const handleCheckboxChange = (productId, checked) => {
        if (checked) {
            setSelectedProductIds((prev) => [...prev, productId]);
        } else {
            setSelectedProductIds((prev) => prev.filter((id) => id !== productId));
        }
    };

    const handleAddSelectedProducts = () => {
        // Find the selected products from searchResults
        const chosen = products.filter((p) => selectedProductIds.includes(p.id));
        // Loop through or map them, call handleAddProduct(p) for each
        const addProductWithDelay = async (product) => {
            await new Promise((resolve) => setTimeout(resolve, 100)); // 100ms delay
            handleAddProduct(product);
        };

        const addProductsSequentially = async () => {
            for (const prod of chosen) {
            await addProductWithDelay(prod);
            }
        };

        addProductsSequentially();
        setIsModalOpen(false);
        setSearchTerm('');
        setSelectedProductIds([]);
    };

    // Handle adding a product to the inventory
    const handleAddProduct = (product) => {
        if (!inventory) return;

        // Check if the product is already in the inventory
        const existingItem = inventory.inventoryItems.find(
            (item) => item.productId === product.id
        );

        if (existingItem) {
            // Highlight and focus on the quantity input field
            dispatch(inventoryActions.addOrUpdateItem(existingItem));
        } else {
            // Prepare the inventory item for saving
            const newItem = {
                productId: product.id,
                inventoryId: inventory.id || 0, // Include inventory ID
                stockReel: product.stock,// Default value
                stockTheorique: product.stock, // Default value
                stockValeurUnitaire: product.buyingPrice, // Use product's buying price
                stockEcartQuantite: 0, // Default value (will be calculated automatically)
                inventoryDate: inventory.inventoryDate, // Include inventory date
                totalStockValue: inventory.totalStockValue, // Include total stock value
                totalItems: inventory.totalItems, // Include total items
            };

            dispatch(inventoryActions.handleAddOrUpdateItem(newItem));
        }
    };

    // Handle deleting an item
    const handleDeleteItem = (id) => {
        dispatch(inventoryActions.handleDeleteItem({ id }));
    };

    // Handle updating stockReel or stockTheorique
    const handleQuantityChange = (productId, currentStockTheorique, valeurUnitaire, newReelValue) => {
        setLocalStockReel((prev) => ({
            ...prev,
            [productId]: {
                reel: newReelValue,
                theorique: currentStockTheorique,
                valeurUnitaire
            }
        }));
    };

    // Handle updating stockReel or stockTheorique
    const handleQuantityOnBlur = (productId) => {
        const localData = localStockReel[productId];
        if (!localData) return;
        const { reel, theorique, valeurUnitaire } = localData;

        const existingItem = inventory.inventoryItems.find(item => item.productId === productId);
        if (existingItem && existingItem.stockReel !== reel) {
            const updatedItem = {
                productId,
                inventoryId: inventory.id || 0,
                stockReel: reel,
                stockTheorique: theorique,
                stockValeurUnitaire: valeurUnitaire,
                stockEcartQuantite: reel - theorique
            };

            dispatch(inventoryActions.updateItemQuantity(updatedItem));
            dispatch(inventoryActions.handleAddOrUpdateItem({
                ...updatedItem,
                inventoryDate: inventory.inventoryDate,
                totalStockValue: inventory.totalStockValue,
                totalItems: inventory.totalItems
            }));

            searchInputRef.current.focus();
        }
    };

    useEffect(() => {   
        if (highlightedItem && quantityInputRef.current) {
            quantityInputRef.current.focus();
        } else if (!loading) {
            searchInputRef.current.focus();
        }
    }, [highlightedItem, loading]);

    const handleShowProductDetails = useCallback((product) => {
        setSelectedProductDetails(product);
    }, []);

    if (loading) {
        return (
            <div
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.3)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 9999,
                }}
            >
                <Spinner color="primary" />
            </div>
        );
    }


    // Handle inventory selection
    const handleSelectInventory = (inventoryId) => {
        dispatch(inventoryActions.fetchInventory(inventoryId));
        setSearchTerm('');
    };



    const debouncedSearch = debounce((term) => {
        dispatch(productActions.search({
            criteria: {
                query: term,
            },
            page: 1,
            size: 10,
        }));
    }, 1000);

    // Handle search input change
    const handleSearchChange = (e) => {
        const term = e.target.value;
        setIsSearching(true);
        setSearchTerm(term);
        debouncedSearch(term);
    };

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        dispatch(productActions.search({
            criteria: { query: searchTerm },
            page: pageNumber,
            size: 10
        }));
    };


    // Update price formatting function
    const formatPrice = (amount) => {
        const formattedPrice = new Intl.NumberFormat('fr-MA', {
            style: 'decimal',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
        return `${formattedPrice} DH`;
    };


    const handleCloseInventory = () => {
        Swal.fire({
            title: t('inventory.confirmClose'),
            text: t('inventory.closeWarning'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: t('common.yesClose'),
            cancelButtonText: t('common.cancel')
        }).then((result) => {
            if (result.isConfirmed) {
                if (inventory && !inventory.closed) {
                    dispatch(inventoryActions.closeInventory(inventory.id));
                }
            }
        });
    };


    const handleSearchKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            dispatch(productActions.search({
                criteria: { searchTerm },
                page: 1,
                size: 10
            }));

        }
    };

    const handleExportToExcel = () => {
        // Create a new workbook
        const wb = XLSX.utils.book_new();

        // Prepare the header row with inventory date and stock value
        const headerRow = [
            `${t('inventory.inventoryDate')}: ${inventory.inventoryDate}`,
            `${t('inventory.currentStockValue')}: ${formatPrice(inventory.totalStockValue)}`,
            `${t('inventory.responsible')}: ${fullName}`,
        ];

        // Prepare the data for Excel
        const data = [
            headerRow, // First row with inventory date and stock value
            [
                t('inventory.codeArticle'),
                t('inventory.description'),
                t('inventory.stockTheorique'),
                t('inventory.valeurUnitaire'),
                t('inventory.stockReel'),
                t('inventory.ecartQuantite'),
            ], // Header row
            ...inventory.inventoryItems.map(item => [
                item.product.barcode,
                item.product.description,
                item.stockTheorique,
                item.stockValeurUnitaire,
                item.stockReel,
                item.stockEcartQuantite,
            ]) // Inventory items
        ];

        // Create a worksheet
        const ws = XLSX.utils.aoa_to_sheet(data);

        // Add the worksheet to the workbook
        XLSX.utils.book_append_sheet(wb, ws, t('inventory.inventorySheet'));

        // Generate the Excel file
        XLSX.writeFile(wb, `${t('inventory.inventorySheet')}_${inventory.inventoryDate}.xlsx`);
    };

    const handleNewInventory = () => {
        const newInventory = {
            id: 0,
            inventoryItems: [],
            totalStockValue: 0,
            totalItems: 0,
            closed: false,
            inventoryDate: moment().format('YYYY-MM-DD'),
        };

        dispatch(inventoryActions.createNewInventory(newInventory));
        dispatch(inventoryActions.saveInventory(newInventory));
    };

    if (error) {
        return <Alert color="danger">{error}</Alert>; // Show error message if there's an error
    }
    // Handle updating the inventory date
    const handleDateChange = (e) => {
        const date = e.target.value;
        dispatch(
            inventoryActions.updateInventoryMetadata({
                inventoryDate: date,
                totalStockValue: inventory.totalStockValue,
                totalItems: inventory.totalItems,
            })
        );

        // Save the updated inventory to the backend
        dispatch(inventoryActions.saveInventory({ ...inventory, inventoryDate: date }));
    };
    return (<>
        <Container fluid>
            <Row className="mb-3">
                <Col>
                    <Card className="shadow-sm border-0">
                        <CardBody className="py-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <h4 className="mb-0 d-flex align-items-center">
                                    <FaBoxes className="text-primary me-2" />
                                    {t('inventory.pageTitle')}
                                </h4>
                                <div className="d-flex align-items-center">
                                    <Dropdown
                                        isOpen={dropdownOpen}
                                        toggle={() => setDropdownOpen(!dropdownOpen)}
                                        className="my-dropdown"
                                    >
                                        <DropdownToggle caret className="btn-sm btn-primary">
                                            {inventory ? `Inventaire #${inventory.id}` : 'Sélectionner'}
                                        </DropdownToggle>
                                        <DropdownMenu className="shadow" style={{ minWidth: '12rem' }}>
                                            {inventories && inventories.map((inv) => (
                                                <DropdownItem key={inv.id} onClick={() => handleSelectInventory(inv.id)}>
                                                    #{inv.id} - {inv.inventoryDate}
                                                </DropdownItem>
                                            ))}
                                        </DropdownMenu>
                                    </Dropdown>
                                    <Button
                                        size="sm"
                                        color="success"
                                        onClick={handleNewInventory}
                                        className="ms-2"
                                    >
                                        <FaPlus className="me-1" />
                                        {t('inventory.newInventory')}
                                    </Button>
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            <Row>
                <Col md={12}>
                    <Card className="shadow-sm h-100">
                        <CardBody > {/* Adjust maxHeight as needed */}
                            {selectedProductDetails && (
                                <div className="mb-3 p-3 bg-light border rounded" style={{ fontSize: '12px' }}>
                                    <Row>

                                        <Col md={4}>
                                            <p>
                                                <strong>{t('product.description')}:</strong> {selectedProductDetails.description}
                                            </p>
                                        </Col>
                                        <Col md={3}>
                                            <p>
                                                <strong>{t('product.buyingPrice')}:</strong> {formatPrice(selectedProductDetails.buyingPrice ?? 0)}
                                            </p>
                                        </Col>
                                        <Col md={3}>
                                            <p>
                                                <strong>{t('product.salePrice')}:</strong> {formatPrice(selectedProductDetails.userPrice ?? 0)}
                                            </p>
                                        </Col>
                                        <Col md={2}>
                                            <p>
                                                <strong>{t('product.stockQty')}:</strong> {selectedProductDetails.stock ?? 0}
                                            </p>
                                        </Col>
                                    </Row>
                                </div>
                            )}

                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5 className="mb-0">{t('inventory.inventorySheet')}</h5>
                                <Badge color={inventory?.closed ? "danger" : "success"} className="me-2">
                                    {inventory?.closed ? t('inventory.closed') : t('inventory.open')}
                                </Badge>
                                <div>
                                    <span className="me-3">{t('inventory.inventoryDate')}:</span>
                                    <Input
                                        type="date"
                                        value={inventory?.inventoryDate || ''}
                                        onChange={handleDateChange}
                                        style={{ fontSize: '12px', width: '150px', display: 'inline-block' }}
                                        disabled={inventory?.closed || loading}

                                    />
                                </div>
                                <div>
                                    <strong style={{ fontSize: '0.9em' }}>
                                        {t('inventory.currentStockValue')}: {formatPrice(inventory?.totalStockValue || 0)}
                                    </strong>
                                </div>
                                <div>
                                    <strong style={{ fontSize: '0.9em' }}>
                                        {t('inventory.totalItems')}: {inventory?.totalItems || 0}
                                    </strong>
                                </div>

                            </div>
                            <div className="position-relative flex-grow-1 me-3 mb-3">
                                <Input
                                    innerRef={searchInputRef}
                                    type="text"
                                    placeholder={t('product.searchFocusHint')}
                                    value={searchTerm}
                                    onKeyDown={handleSearchKeyDown}
                                    onChange={handleSearchChange}
                                    className={`ps-4 pe-5 ${isSearching ? 'searching' : ''}`}
                                    title={t('product.searchShortcut')}
                                    disabled={inventory?.closed || loading} // Disable if inventory is closed
                                />
                                <FaSearch
                                    style={{
                                        position: 'absolute',
                                        left: '10px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: '#adb5bd'
                                    }}
                                    size={14}
                                />
                                <FaBarcode
                                    style={{
                                        position: 'absolute',
                                        right: '10px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: '#adb5bd'
                                    }}
                                    size={20}
                                    title={t('product.scanHint')}
                                />
                            </div>
                            <div style={{ overflowY: 'auto', maxHeight: '400px' }}>
                                <Table bordered hover style={{ fontSize: '14px', fontWeight: 'bold' }}>
                                    <thead>
                                        <tr style={{ whiteSpace: 'nowrap' }}>
                                            <th>{t('inventory.codeArticle')}</th>
                                            <th style={{ width: '30%' }}>{t('inventory.description')}</th> {/* Increased width */}
                                            <th>{t('inventory.stockTheorique')}</th>
                                            <th>{t('inventory.stockReel')}</th>
                                            <th style={{ width: '20%' }}>{t('inventory.valeurUnitaire')}</th>
                                            <th>{t('inventory.ecartQuantite')}</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {inventory?.inventoryItems.map((item) => (
                                            <tr
                                                key={item.product.id}
                                                onClick={() => handleShowProductDetails(item.product)}
                                                className={highlightedItem === item.id ? 'table-primary' : 'table-light'} // Highlight the row
                                                style={{ whiteSpace: 'nowrap', cursor: 'pointer', height: '20px' }}
                                            >
                                                <td>{item.product.barcode}</td>
                                                <td>
                                                    {item.product.description.length > 20 ? item.product.description.substring(0, 20) + '...' : item.product.description}
                                                </td>
                                                <td>{item.stockTheorique}</td>
                                                <td>
                                                    <Input
                                                        type="number"
                                                        value={localStockReel[item.productId]?.reel ?? item.stockReel}
                                                        size="sm"
                                                        innerRef={highlightedItem === item.id ? quantityInputRef : null}
                                                        onChange={(e) =>
                                                            handleQuantityChange(
                                                                item.productId,
                                                                item.stockTheorique,
                                                                item.stockValeurUnitaire,
                                                                parseInt(e.target.value) || 0
                                                            )
                                                        }
                                                        onFocus={(e) => e.target.select()} // Select the field content on focus
                                                        onBlur={() => handleQuantityOnBlur(item.productId)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') {
                                                                e.preventDefault();
                                                                searchInputRef.current?.focus();
                                                            }
                                                        }}
                                                        disabled={inventory.closed || loading}
                                                    />
                                                </td>
                                                <td>{item.stockValeurUnitaire}</td>
                                                <td>{item.stockEcartQuantite}</td>
                                                <td>
                                                    <Button
                                                        color="danger"
                                                        size="sm"
                                                        onClick={() => handleDeleteItem(item.id)}
                                                        disabled={inventory.closed}>
                                                        <FaTrash />
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>

                            {/* <Button size="sm" color="success" onClick={handleSubmitInventory}
                                    disabled={currentInventory.closed || currentInventory.inventoryItems.length === 0 || currentInventory.inventoryItems.some(item => item.stockReel === 0)}
                                >
                                    {t('common.save')} <FaSave className="ms-1" />
                                </Button> */}
                            <Button size="sm" color="warning" onClick={handleCloseInventory}
                                disabled={inventory?.closed || loading}
                                className="ms-2">
                                <FaLock className="me-1" />
                                {t('inventory.closeInventory')}
                            </Button>
                            <Button size="sm" color="info" onClick={handleExportToExcel} className="ms-2" disabled={!inventory || inventory.inventoryItems.length === 0}>
                                <FaFileExcel className="me-1" />
                                {t('common.exportExcel')}
                            </Button>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </Container>
        {isModalOpen && (
            <Modal isOpen={isModalOpen} toggle={() => setIsModalOpen(false)} size='lg'>
                <ModalHeader toggle={() => setIsModalOpen(false)}>
                    {t('common.searchResults')}
                </ModalHeader>
                <ModalBody >
                    <div className="cart-container" style={{
                        height: '500px',
                        overflowY: 'auto'
                    }}>
                        <Table bordered hover>
                            <thead>
                                <tr>
                                    <th></th>
                                    <th>{t('inventory.codeArticle')}</th>
                                    <th style={{ width: '50%' }}>{t('product.description')}</th>
                                    <th>{t('product.buyingPrice')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {products.map((product) => (
                                    <tr key={product.id} style={{ whiteSpace: 'nowrap', height: '20px' }}>
                                        <td>
                                            <Input
                                                type="checkbox"
                                                checked={selectedProductIds.includes(product.id)}
                                                onChange={(e) => handleCheckboxChange(product.id, e.target.checked)}
                                            />
                                        </td>
                                        <td>{product.barcode}</td>
                                        <td>{product.description}</td>
                                        <td>{formatPrice(product.buyingPrice)}</td>
                                    </tr>
                                ))}
                            </tbody>
                                
                        </Table>
                        <div className="d-flex align-items-center gap-2">
                                    <Button color="light" size="sm" onClick={() => handlePageChange(1)} disabled={currentPage === 1}>
                                        <FaAngleDoubleLeft />
                                    </Button>
                                    <Button color="light" size="sm" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
                                        <FaAngleLeft />
                                    </Button>

                                    <span className="mx-2">
                                        {currentPage} / {totalPages}
                                    </span>

                                    <Button color="light" size="sm" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>
                                        <FaAngleRight />
                                    </Button>
                                    <Button color="light" size="sm" onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages}>
                                        <FaAngleDoubleRight />
                                    </Button>
                                </div>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={handleAddSelectedProducts} disabled={selectedProductIds.length === 0}>
                        {t('common.addSelected')}
                    </Button>
                </ModalFooter>
            </Modal>
        )}
    </>
    );
};

export default InventoryPage;